import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import backendApi from "../api/backendApi";
import AnalyticsBreakdownTable from "../components/adminAnalytics/AnalyticsBreakdownTable";
import AnalyticsFilterBar from "../components/adminAnalytics/AnalyticsFilterBar";
import AnalyticsKpiCards from "../components/adminAnalytics/AnalyticsKpiCards";
import AnalyticsMuqamiRankingTable from "../components/adminAnalytics/AnalyticsMuqamiRankingTable";
import AnalyticsPdfExportAction from "../components/adminAnalytics/AnalyticsPdfExportAction";
import AnalyticsPrimaryChart from "../components/adminAnalytics/AnalyticsPrimaryChart";
import AnalyticsRebuildAction from "../components/adminAnalytics/AnalyticsRebuildAction";
import AnalyticsRebuildModal from "../components/adminAnalytics/AnalyticsRebuildModal";
import { protectedTheme } from "../components/ui/protectedTheme";
import {
  ALL_CATEGORIES,
  ALL_MUQAMI,
  ANALYTICS_CATEGORIES,
  MONTH_OPTIONS,
  VIEW_OPTIONS,
  averageValues,
  buildYearOptions,
  clamp,
  formatCategoryName,
  formatCompactCurrency,
  formatCurrency,
  formatMuqamiName,
  getKualaLumpurDateParts,
  sumValues,
} from "../components/adminAnalytics/analyticsUtils";

const DEFAULT_CATEGORY = "KHUDDAM_IJTIMA";
const DEFAULT_VIEW = "trend";
const YEAR_START = 2025;
const DEFAULT_TREND_CARDS = [
  { label: "Selected Range Total" },
  { label: "Average Per Month" },
  { label: "Peak Month" },
  { label: "Months Covered" },
];
const DEFAULT_COMPARE_CARDS = [
  { label: "Selected Range Total" },
  { label: "Selected Category" },
  { label: "Top Category" },
  { label: "Transactions" },
];
const DEFAULT_MUQAMI_CARDS = [
  { label: "Total Collections" },
  { label: "Top Muqami" },
  { label: "Active Muqamis" },
  { label: "Average / Active Muqami" },
];

const CHART_COLORS = {
  KHUDDAM_IJTIMA: "#111827",
  KHUDDAM_MEMBERSHIP: "#1d4ed8",
  ATHFAL_IJTIMA: "#0f766e",
  ATHFAL_MEMBERSHIP: "#0891b2",
  HALL: "#7c3aed",
  LOCAL_FUND: "#2563eb",
  KHIDMAT_KHALAQ: "#c2410c",
  TRX_FEE: "#475569",
  TAX: "#be123c",
};

function parseInteger(value) {
  const parsed = Number.parseInt(value ?? "", 10);
  return Number.isNaN(parsed) ? null : parsed;
}

function buildSearchParamsFromFilters(filters) {
  const params = new URLSearchParams();
  params.set("year", String(filters.year));
  params.set("category", filters.category);
  params.set("view", filters.view);
  params.set("muqami", filters.muqami || ALL_MUQAMI);
  params.set("fromMonth", String(filters.fromMonth));
  params.set("toMonth", String(filters.toMonth));
  params.set("includeLocalFund", filters.includeLocalFund ? "1" : "0");
  if (filters.compareYear) {
    params.set("compareYear", String(filters.compareYear));
  }
  return params;
}

function normalizeFilters(searchParams) {
  const { currentYear, currentMonth } = getKualaLumpurDateParts();
  const yearOptions = buildYearOptions(YEAR_START);

  const yearParam = parseInteger(searchParams.get("year"));
  const year = yearOptions.includes(yearParam) ? yearParam : currentYear;
  const maxMonth = year === currentYear ? currentMonth : 12;

  const viewParam = searchParams.get("view");
  const view = VIEW_OPTIONS.some((option) => option.value === viewParam)
    ? viewParam
    : DEFAULT_VIEW;

  const categoryParam = searchParams.get("category");
  const category =
    view === "muqami" && categoryParam === ALL_CATEGORIES
      ? ALL_CATEGORIES
      : ANALYTICS_CATEGORIES.includes(categoryParam)
      ? categoryParam
      : view === "muqami"
      ? ALL_CATEGORIES
      : DEFAULT_CATEGORY;

  const muqami = (searchParams.get("muqami") || ALL_MUQAMI).toUpperCase();

  const defaultToMonth = year === currentYear ? currentMonth : 12;
  const fromMonth = clamp(parseInteger(searchParams.get("fromMonth")) ?? 1, 1, maxMonth);
  const toMonth = clamp(
    parseInteger(searchParams.get("toMonth")) ?? defaultToMonth,
    fromMonth,
    maxMonth
  );

  const compareYearParam = parseInteger(searchParams.get("compareYear"));
  const compareYear =
    compareYearParam &&
    yearOptions.includes(compareYearParam) &&
    compareYearParam !== year &&
    muqami === ALL_MUQAMI
      ? compareYearParam
      : "";

  return {
    year,
    category: view === "muqami" ? category : category === ALL_CATEGORIES ? DEFAULT_CATEGORY : category,
    view,
    muqami: view === "muqami" ? ALL_MUQAMI : muqami,
    fromMonth,
    toMonth,
    compareYear,
    includeLocalFund: searchParams.get("includeLocalFund") === "1",
    maxMonth,
    currentYear,
    currentMonth,
  };
}

function getMuqamiParams(muqami) {
  return muqami && muqami !== ALL_MUQAMI
    ? { muqami }
    : {};
}

function getMonthRange(fromMonth, toMonth) {
  return MONTH_OPTIONS.filter(
    (month) => month.value >= fromMonth && month.value <= toMonth
  );
}

function getSeriesMonths(series) {
  if (Array.isArray(series?.months) && series.months.length) {
    return series.months.map((entry, index) => ({
      paymentMonth: entry.paymentMonth || "",
      month: Number(entry.month ?? index + 1),
      label:
        entry.label ||
        MONTH_OPTIONS.find((month) => month.value === Number(entry.month))
          ?.label ||
        `M${index + 1}`,
      totalAmount: Number(entry.totalAmount ?? 0),
      transactionCount: Number(entry.transactionCount ?? 0),
    }));
  }

  return MONTH_OPTIONS.map((month, index) => ({
    paymentMonth: `${series?.year ?? ""}-${String(month.value).padStart(2, "0")}`,
    month: month.value,
    label: month.label,
    totalAmount: Number(series?.values?.[index] ?? 0),
    transactionCount: 0,
  }));
}

function sliceSeriesPoints(series, fromMonth, toMonth) {
  return getSeriesMonths(series).filter(
    (month) => month.month >= fromMonth && month.month <= toMonth
  );
}

function getTrendRows(points, comparePoints = []) {
  const compareByMonth = new Map(
    comparePoints.map((point) => [point.month, point])
  );

  return points.map((point) => {
    const comparePoint = compareByMonth.get(point.month);
    const compareValue = comparePoint?.totalAmount ?? 0;
    return {
      month: point.label,
      paymentMonth: point.paymentMonth,
      transactionCount: point.transactionCount,
      value: point.totalAmount,
      compareValue,
      delta: point.totalAmount - compareValue,
    };
  });
}

function formatPaymentMonth(year, month) {
  return `${year}-${String(month).padStart(2, "0")}`;
}

function formatRebuildRange(startYear, startMonth, endYear, endMonth) {
  const start = formatPaymentMonth(startYear, startMonth);
  const end = formatPaymentMonth(endYear, endMonth);
  return start === end ? start : `${start} to ${end}`;
}

function buildFilteredRebuildScope(year, fromMonth, toMonth) {
  return {
    kind: "filtered",
    title: "Rebuild Selected Range",
    confirmLabel: "Rebuild Selected Range",
    rangeLabel: formatRebuildRange(year, fromMonth, year, toMonth),
    description:
      "Rebuild only the reporting months currently selected in the dashboard. Category filters do not narrow rebuild scope.",
    params: {
      startYear: year,
      startMonth: fromMonth,
      endYear: year,
      endMonth: toMonth,
    },
  };
}

function buildFullRebuildScope(currentYear, currentMonth) {
  return {
    kind: "full",
    title: "Full Historical Rebuild",
    confirmLabel: "Start Full Rebuild",
    rangeLabel: formatRebuildRange(
      YEAR_START,
      1,
      currentYear,
      currentMonth
    ),
    description:
      "Rebuild all persisted monthly analytics from January 2025 through the current Kuala Lumpur month.",
    params: {
      startYear: YEAR_START,
      startMonth: 1,
      endYear: currentYear,
      endMonth: currentMonth,
    },
  };
}

function formatRebuildFeedback(summary, scope) {
  const monthsProcessed = Number(summary?.monthsProcessed ?? 0);
  const rowsUpserted = Number(summary?.rowsUpserted ?? 0);
  const monthLabel = monthsProcessed === 1 ? "month" : "months";
  const rowLabel = rowsUpserted === 1 ? "summary row" : "summary rows";

  return `${
    scope.kind === "full" ? "Full historical rebuild" : "Selected range rebuild"
  } completed for ${scope.rangeLabel}. Processed ${monthsProcessed.toLocaleString(
    "en-MY"
  )} ${monthLabel} and upserted ${rowsUpserted.toLocaleString(
    "en-MY"
  )} ${rowLabel}.`;
}

function getFilenameFromContentDisposition(contentDisposition) {
  const match = /filename="?([^";]+)"?/i.exec(contentDisposition || "");
  return match?.[1] || "";
}

async function getDownloadErrorMessage(error) {
  const fallback = error?.message || "Failed to export analytics PDF.";
  const data = error?.response?.data;

  if (data instanceof Blob) {
    try {
      const text = await data.text();
      const parsed = JSON.parse(text);
      return parsed?.message || fallback;
    } catch {
      return fallback;
    }
  }

  return data?.message || fallback;
}

const AdminDashboardPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const filters = useMemo(() => normalizeFilters(searchParams), [searchParams]);
  const yearOptions = useMemo(
    () => buildYearOptions(YEAR_START, filters.currentYear),
    [filters.currentYear]
  );

  const [trendState, setTrendState] = useState({
    loading: false,
    error: "",
    primary: null,
    compare: null,
  });
  const [breakdownState, setBreakdownState] = useState({
    loading: false,
    error: "",
    data: null,
  });
  const [muqamiOptions, setMuqamiOptions] = useState([ALL_MUQAMI]);
  const [muqamiRankingState, setMuqamiRankingState] = useState({
    loading: false,
    error: "",
    data: null,
  });
  const [analyticsRefreshKey, setAnalyticsRefreshKey] = useState(0);
  const [rebuildState, setRebuildState] = useState({
    modalOpen: false,
    scope: null,
    submitting: false,
    errorMessage: "",
    feedback: null,
  });
  const [pdfExportState, setPdfExportState] = useState({
    loading: false,
    error: "",
  });

  useEffect(() => {
    const canonicalParams = buildSearchParamsFromFilters(filters).toString();
    if (canonicalParams !== searchParams.toString()) {
      setSearchParams(canonicalParams, { replace: true });
    }
  }, [filters, searchParams, setSearchParams]);

  useEffect(() => {
    let ignore = false;

    const loadMuqamis = async () => {
      try {
        const response = await backendApi.get("/muqamis");
        const options = Array.isArray(response.data) ? response.data : [];
        if (!ignore) {
          setMuqamiOptions([ALL_MUQAMI, ...options.filter(Boolean)]);
        }
      } catch {
        if (!ignore) setMuqamiOptions([ALL_MUQAMI]);
      }
    };

    loadMuqamis();
    return () => {
      ignore = true;
    };
  }, []);

  const handleFilterChange = (key, value) => {
    const next = { ...filters, [key]: value };

    if (key === "year") {
      const maxMonth = value === filters.currentYear ? filters.currentMonth : 12;
      next.fromMonth = clamp(next.fromMonth, 1, maxMonth);
      next.toMonth = clamp(next.toMonth, next.fromMonth, maxMonth);
      if (next.compareYear === value) next.compareYear = "";
    }

    if (key === "view" && value === "muqami") {
      next.category = ALL_CATEGORIES;
      next.muqami = ALL_MUQAMI;
      next.compareYear = "";
    }

    if (key === "view" && value !== "muqami" && next.category === ALL_CATEGORIES) {
      next.category = DEFAULT_CATEGORY;
    }

    if (key === "muqami" && value !== ALL_MUQAMI) {
      next.compareYear = "";
    }

    if (key === "fromMonth") {
      next.toMonth = Math.max(value, next.toMonth);
    }

    if (key === "toMonth") {
      next.fromMonth = Math.min(value, next.fromMonth);
    }

    if (key === "compareYear" && value === next.year) {
      next.compareYear = "";
    }

    setSearchParams(buildSearchParamsFromFilters(next));
  };

  const handleReset = () => {
    const { currentYear, currentMonth } = getKualaLumpurDateParts();
    setSearchParams(
      buildSearchParamsFromFilters({
        year: currentYear,
        category: DEFAULT_CATEGORY,
        view: DEFAULT_VIEW,
        muqami: ALL_MUQAMI,
        fromMonth: 1,
        toMonth: currentMonth,
        compareYear: "",
        includeLocalFund: false,
      })
    );
  };

  const filteredRebuildScope = useMemo(
    () => buildFilteredRebuildScope(filters.year, filters.fromMonth, filters.toMonth),
    [filters.year, filters.fromMonth, filters.toMonth]
  );
  const fullRebuildScope = useMemo(
    () => buildFullRebuildScope(filters.currentYear, filters.currentMonth),
    [filters.currentYear, filters.currentMonth]
  );

  const openRebuildModal = (scope) => {
    setRebuildState((current) => ({
      ...current,
      modalOpen: true,
      scope,
      submitting: false,
      errorMessage: "",
    }));
  };

  const closeRebuildModal = () => {
    setRebuildState((current) =>
      current.submitting
        ? current
        : {
            ...current,
            modalOpen: false,
            scope: null,
            errorMessage: "",
          }
    );
  };

  const handleConfirmRebuild = async () => {
    if (!rebuildState.scope || rebuildState.submitting) return;
    const scope = rebuildState.scope;

    setRebuildState((current) => ({
      ...current,
      submitting: true,
      errorMessage: "",
      feedback: null,
    }));

    try {
      const response = await backendApi.post("/admin/analytics/rebuild", null, {
        params: scope.params,
      });
      const feedbackMessage = formatRebuildFeedback(response.data, scope);

      setRebuildState({
        modalOpen: false,
        scope: null,
        submitting: false,
        errorMessage: "",
        feedback: {
          tone: "success",
          message: feedbackMessage,
        },
      });
      setAnalyticsRefreshKey((value) => value + 1);
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        error.message ||
        "Failed to rebuild analytics.";

      setRebuildState((current) => ({
        ...current,
        submitting: false,
        errorMessage: message,
        feedback: {
          tone: "error",
          message,
        },
      }));
    }
  };

  const handleExportPdf = async () => {
    if (pdfExportState.loading) return;

    setPdfExportState({ loading: true, error: "" });

    try {
      const response = await backendApi.get("/admin/analytics/revenue/report.pdf", {
        params: {
          year: filters.year,
          fromMonth: filters.fromMonth,
          toMonth: filters.toMonth,
          ...getMuqamiParams(filters.muqami),
        },
        responseType: "blob",
      });

      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      const fallbackFilename = `admin-analytics-${filteredRebuildScope.rangeLabel.replace(
        /\s+to\s+/,
        "-to-"
      )}.pdf`;

      link.href = url;
      link.download =
        getFilenameFromContentDisposition(response.headers?.["content-disposition"]) ||
        fallbackFilename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      setPdfExportState({ loading: false, error: "" });
    } catch (error) {
      const message = await getDownloadErrorMessage(error);
      setPdfExportState({ loading: false, error: message });
    }
  };

  useEffect(() => {
    if (filters.view !== "trend") return;

    let ignore = false;

    const loadTrend = async () => {
      setTrendState({ loading: true, error: "", primary: null, compare: null });
      try {
        const requests = [
          backendApi.get("/admin/analytics/revenue/monthly", {
            params: {
              category: filters.category,
              year: filters.year,
              ...getMuqamiParams(filters.muqami),
            },
          }),
        ];

        if (filters.compareYear && filters.muqami === ALL_MUQAMI) {
          requests.push(
            backendApi.get("/admin/analytics/revenue/monthly", {
              params: { category: filters.category, year: filters.compareYear },
            })
          );
        }

        const [primary, compare] = await Promise.all(requests);
        if (!ignore) {
          setTrendState({
            loading: false,
            error: "",
            primary: primary.data,
            compare: compare?.data ?? null,
          });
        }
      } catch (error) {
        if (!ignore) {
          setTrendState({
            loading: false,
            error:
              error?.response?.data?.message ||
              error.message ||
              "Failed to load analytics trend",
            primary: null,
            compare: null,
          });
        }
      }
    };

    loadTrend();
    return () => {
      ignore = true;
    };
  }, [
    analyticsRefreshKey,
    filters.view,
    filters.category,
    filters.muqami,
    filters.year,
    filters.compareYear,
  ]);

  useEffect(() => {
    if (filters.view !== "compare") return;

    let ignore = false;

    const loadBreakdown = async () => {
      setBreakdownState({ loading: true, error: "", data: null });
      try {
        const response = await backendApi.get("/admin/analytics/revenue/breakdown", {
          params: {
            year: filters.year,
            fromMonth: filters.fromMonth,
            toMonth: filters.toMonth,
            ...getMuqamiParams(filters.muqami),
          },
        });
        if (!ignore) {
          setBreakdownState({
            loading: false,
            error: "",
            data: response.data,
          });
        }
      } catch (error) {
        if (!ignore) {
          setBreakdownState({
            loading: false,
            error:
              error?.response?.data?.message ||
              error.message ||
              "Failed to load analytics breakdown",
            data: null,
          });
        }
      }
    };

    loadBreakdown();
    return () => {
      ignore = true;
    };
  }, [
    analyticsRefreshKey,
    filters.view,
    filters.muqami,
    filters.year,
    filters.fromMonth,
    filters.toMonth,
  ]);

  useEffect(() => {
    if (filters.view !== "muqami") return;

    let ignore = false;

    const loadRanking = async () => {
      setMuqamiRankingState({ loading: true, error: "", data: null });
      try {
        const response = await backendApi.get("/admin/analytics/revenue/by-muqami", {
          params: {
            year: filters.year,
            fromMonth: filters.fromMonth,
            toMonth: filters.toMonth,
            category: filters.category,
          },
        });
        if (!ignore) {
          setMuqamiRankingState({
            loading: false,
            error: "",
            data: response.data,
          });
        }
      } catch (error) {
        if (!ignore) {
          setMuqamiRankingState({
            loading: false,
            error:
              error?.response?.data?.message ||
              error.message ||
              "Failed to load Muqami ranking",
            data: null,
          });
        }
      }
    };

    loadRanking();
    return () => {
      ignore = true;
    };
  }, [
    analyticsRefreshKey,
    filters.view,
    filters.category,
    filters.year,
    filters.fromMonth,
    filters.toMonth,
  ]);

  const selectedMonths = useMemo(
    () => getMonthRange(filters.fromMonth, filters.toMonth),
    [filters.fromMonth, filters.toMonth]
  );

  const trendViewModel = useMemo(() => {
    if (!trendState.primary) return null;

    const primaryPoints = sliceSeriesPoints(
      trendState.primary,
      filters.fromMonth,
      filters.toMonth
    );
    const comparePoints = filters.compareYear && trendState.compare
      ? sliceSeriesPoints(trendState.compare, filters.fromMonth, filters.toMonth)
      : null;
    const labels = primaryPoints.map((point) => point.label);
    const primaryValues = primaryPoints.map((point) => point.totalAmount);
    const compareValues = comparePoints
      ? comparePoints.map((point) => point.totalAmount)
      : null;
    const rangeStartPaymentMonth = primaryPoints[0]?.paymentMonth;
    const rangeEndPaymentMonth =
      primaryPoints[primaryPoints.length - 1]?.paymentMonth;

    const total = sumValues(primaryValues);
    const average = averageValues(primaryValues);
    const peakValue = Math.max(...primaryValues, 0);
    const peakIndex = primaryValues.findIndex((value) => value === peakValue);
    const compareTotal = compareValues ? sumValues(compareValues) : 0;
    const deltaAmount = total - compareTotal;
    const deltaPercent =
      compareValues && compareTotal !== 0
        ? `${deltaAmount >= 0 ? "+" : ""}${((deltaAmount / compareTotal) * 100).toFixed(1)}%`
        : compareValues
        ? "New"
        : `${labels.length}`;

    return {
        chartData: {
          labels,
          datasets: [
            {
              label: `${formatCategoryName(filters.category)} ${filters.year}`,
              data: primaryValues,
              borderColor: CHART_COLORS[filters.category] || "#111827",
              backgroundColor: "rgba(17, 24, 39, 0.12)",
              fill: true,
              tension: 0.32,
            },
            ...(compareValues
              ? [
                  {
                    label: `${formatCategoryName(filters.category)} ${filters.compareYear}`,
                    data: compareValues,
                    borderColor: "#0f766e",
                    backgroundColor: "rgba(15, 118, 110, 0.08)",
                    borderDash: [6, 6],
                    fill: false,
                    tension: 0.32,
                  },
                ]
              : []),
          ],
        },
        rows: getTrendRows(primaryPoints, comparePoints ?? []),
        cards: [
          {
            label: "Selected Range Total",
            value: total,
            tone: "currency",
            helpText:
              rangeStartPaymentMonth && rangeEndPaymentMonth
                ? `${rangeStartPaymentMonth} to ${rangeEndPaymentMonth}`
                : `${selectedMonths[0]?.label} to ${
                    selectedMonths[selectedMonths.length - 1]?.label
                  } ${filters.year}`,
          },
          {
            label: "Average Per Month",
            value: average,
            tone: "currency",
            helpText: `${labels.length} months included`,
          },
          {
            label: "Peak Month",
            value: peakValue,
            tone: "currency",
            helpText:
              peakIndex >= 0 && peakValue > 0
                ? `${primaryPoints[peakIndex]?.paymentMonth || labels[peakIndex]}`
                : "No collected amount",
          },
          {
            label: filters.compareYear
              ? "Year-over-Year Change"
              : "Months Covered",
            value: deltaPercent,
            helpText: filters.compareYear
              ? `Delta ${formatCompactCurrency(deltaAmount)} vs ${filters.compareYear}`
              : `${labels.length} selected months`,
          },
        ],
      };
  }, [
    filters.category,
    filters.compareYear,
    filters.fromMonth,
    filters.toMonth,
    filters.year,
    selectedMonths,
    trendState.compare,
    trendState.primary,
  ]);

  const compareViewModel = useMemo(() => {
    if (!breakdownState.data) return null;

    const rawItems = breakdownState.data.items ?? [];
    const visibleItems = rawItems
      .filter((item) => filters.includeLocalFund || item.category !== "LOCAL_FUND")
      .map((item) => ({
        ...item,
        totalAmount: Number(item.totalAmount ?? 0),
        totalTransactions: Number(item.totalTransactions ?? 0),
        averageAmount: Number(item.averageAmount ?? 0),
      }))
      .sort((left, right) => right.totalAmount - left.totalAmount);

    const totalVisibleAmount = visibleItems.reduce(
      (sum, item) => sum + item.totalAmount,
      0
    );
    const totalVisibleTransactions = visibleItems.reduce(
      (sum, item) => sum + item.totalTransactions,
      0
    );
    const topCategory = visibleItems[0];
    const selectedCategoryItem = visibleItems.find(
      (item) => item.category === filters.category
    );

    const rows = visibleItems.map((item) => ({
      ...item,
      share: totalVisibleAmount
        ? (item.totalAmount / totalVisibleAmount) * 100
        : 0,
    }));

    return {
      chartData: rows.length
        ? {
            labels: rows.map((row) => row.label),
            datasets: [
              {
                data: rows.map((row) => row.totalAmount),
                backgroundColor: rows.map((row) =>
                  row.category === filters.category
                    ? "#111827"
                    : CHART_COLORS[row.category] || "#64748b"
                ),
                borderRadius: 10,
              },
            ],
          }
        : null,
      rows,
      cards: [
        {
          label: "Selected Range Total",
          value: totalVisibleAmount,
          tone: "currency",
          helpText: `${selectedMonths[0]?.label} to ${
            selectedMonths[selectedMonths.length - 1]?.label
          } ${filters.year}`,
        },
        {
          label: "Selected Category",
          value: selectedCategoryItem?.totalAmount ?? 0,
          tone: "currency",
          helpText: selectedCategoryItem
            ? selectedCategoryItem.label
            : `${formatCategoryName(filters.category)} is not visible in this view`,
        },
        {
          label: "Top Category",
          value: topCategory?.label ?? "-",
          helpText: topCategory
            ? formatCurrency(topCategory.totalAmount)
            : "No analytics data",
        },
        {
          label: "Transactions",
          value: totalVisibleTransactions.toLocaleString("en-MY"),
          helpText:
            totalVisibleTransactions > 0
              ? `Avg ${formatCurrency(
                  totalVisibleAmount / totalVisibleTransactions
                )} per transaction`
              : "No successful transactions",
        },
      ],
    };
  }, [
    breakdownState.data,
    filters.category,
    filters.includeLocalFund,
    filters.year,
    selectedMonths,
  ]);

  const muqamiRankingViewModel = useMemo(() => {
    if (!muqamiRankingState.data) return null;

    const rankingCategory = muqamiRankingState.data.category || filters.category;
    const isAllCategoryRanking = rankingCategory === ALL_CATEGORIES;
    const shouldExcludeLocalFund =
      isAllCategoryRanking && !filters.includeLocalFund;
    const rows = (muqamiRankingState.data.items ?? [])
      .map((item) => {
        const categoryTotals = item.categoryTotals ?? {};
        const localFundAmount = Number(categoryTotals.LOCAL_FUND ?? 0);
        const amount = Number(item.overallAmount ?? 0) -
          (shouldExcludeLocalFund ? localFundAmount : 0);
        return {
          ...item,
          amount: Math.max(amount, 0),
          overallTransactions: Number(item.overallTransactions ?? 0),
          averageAmount: Number(item.averageAmount ?? 0),
        };
      })
      .sort((left, right) => {
        if (right.amount !== left.amount) return right.amount - left.amount;
        return String(left.muqamiLabel || "").localeCompare(
          String(right.muqamiLabel || ""),
          "en",
          { sensitivity: "base" }
        );
      });

    const total = rows.reduce((sum, row) => sum + row.amount, 0);
    const activeRows = rows.filter((row) => row.amount > 0);
    const topMuqami = activeRows[0];

    return {
      chartData: rows.length
        ? {
            labels: rows.map((row) => formatMuqamiName(row.muqamiLabel)),
            datasets: [
              {
                data: rows.map((row) => row.amount),
                backgroundColor: rows.map((row, index) =>
                  index === 0 ? "#bef264" : "#064e3b"
                ),
                borderRadius: 10,
              },
            ],
          }
        : null,
      rows,
      cards: [
        {
          label: "Total Collections",
          value: total,
          tone: "currency",
          helpText: `${formatCategoryName(rankingCategory)} from ${selectedMonths[0]?.label} to ${
            selectedMonths[selectedMonths.length - 1]?.label
          } ${filters.year}`,
        },
        {
          label: "Top Muqami",
          value: topMuqami ? formatMuqamiName(topMuqami.muqamiLabel) : "-",
          helpText: topMuqami ? formatCurrency(topMuqami.amount) : "No collected amount",
        },
        {
          label: "Active Muqamis",
          value: activeRows.length.toLocaleString("en-MY"),
          helpText: `${rows.length.toLocaleString("en-MY")} total Muqamis`,
        },
        {
          label: "Average / Active Muqami",
          value: activeRows.length ? total / activeRows.length : 0,
          tone: "currency",
          helpText: !isAllCategoryRanking
            ? formatCategoryName(rankingCategory)
            : filters.includeLocalFund
            ? "Local Fund included"
            : "Local Fund excluded",
        },
      ],
      category: rankingCategory,
      categoryLabel: formatCategoryName(rankingCategory),
      includeLocalFundNote: !shouldExcludeLocalFund,
    };
  }, [
    filters.category,
    filters.includeLocalFund,
    filters.year,
    muqamiRankingState.data,
    selectedMonths,
  ]);

  const activeViewModel =
    filters.view === "trend"
      ? trendViewModel
      : filters.view === "muqami"
      ? muqamiRankingViewModel
      : compareViewModel;
  const chartLoading =
    filters.view === "trend"
      ? trendState.loading
      : filters.view === "muqami"
      ? muqamiRankingState.loading
      : breakdownState.loading;
  const chartError =
    filters.view === "trend"
      ? trendState.error
      : filters.view === "muqami"
      ? muqamiRankingState.error
      : breakdownState.error;
  const kpiCards =
    activeViewModel?.cards ??
    (filters.view === "trend"
      ? DEFAULT_TREND_CARDS
      : filters.view === "muqami"
      ? DEFAULT_MUQAMI_CARDS
      : DEFAULT_COMPARE_CARDS);

  return (
    <div className={protectedTheme.page}>
      <div className={protectedTheme.shell}>
      <div className="flex flex-col gap-2">
        <p className={protectedTheme.eyebrow}>Admin Reporting</p>
        <h1 className={`${protectedTheme.title} mt-1`}>
          Admin Analytics
        </h1>
        <p className="text-sm text-zinc-500">
          Explore reporting trends and category comparisons using payment-month
          buckets from the backend analytics API.
        </p>
      </div>

      <AnalyticsFilterBar
        filters={filters}
        yearOptions={yearOptions}
        maxMonth={filters.maxMonth}
        muqamiOptions={muqamiOptions}
        onChange={handleFilterChange}
        onReset={handleReset}
      />

      <AnalyticsPdfExportAction
        rangeLabel={filteredRebuildScope.rangeLabel}
        scopeLabel={formatMuqamiName(filters.muqami)}
        loading={pdfExportState.loading}
        error={pdfExportState.error}
        onExport={handleExportPdf}
      />

      <AnalyticsRebuildAction
        filteredRangeLabel={filteredRebuildScope.rangeLabel}
        fullRangeLabel={fullRebuildScope.rangeLabel}
        onOpenFiltered={() => openRebuildModal(filteredRebuildScope)}
        onOpenFull={() => openRebuildModal(fullRebuildScope)}
        disabled={rebuildState.submitting}
        feedback={rebuildState.feedback}
      />

      <AnalyticsKpiCards
        items={kpiCards}
        loading={chartLoading}
        error={chartError}
      />

      <AnalyticsPrimaryChart
        view={filters.view}
        chartData={activeViewModel?.chartData ?? null}
        loading={chartLoading}
        error={chartError}
        title={
          filters.view === "trend"
            ? `${formatCategoryName(filters.category)} trend`
            : filters.view === "muqami"
            ? `${activeViewModel?.categoryLabel || "All Categories"} Muqami ranking`
            : "Category comparison"
        }
        subtitle={
          filters.view === "trend"
            ? `Monthly totals by payment month for ${formatMuqamiName(
                filters.muqami
              )} in ${filters.year}${
                filters.compareYear ? ` compared with ${filters.compareYear}` : ""
              }.`
            : filters.view === "muqami"
            ? `Ranked Muqami collections for ${
                activeViewModel?.categoryLabel || "All Categories"
              } from ${selectedMonths[0]?.label} to ${
                selectedMonths[selectedMonths.length - 1]?.label
              } ${filters.year}.`
            : `Totals across payment months ${selectedMonths[0]?.label} to ${
                selectedMonths[selectedMonths.length - 1]?.label
              } ${filters.year} for ${formatMuqamiName(filters.muqami)}.`
        }
        emptyMessage="No analytics data for the selected filters."
      />

      {filters.view === "muqami" ? (
        <AnalyticsMuqamiRankingTable
          rows={activeViewModel?.rows ?? []}
          loading={chartLoading}
          error={chartError}
          includeLocalFund={activeViewModel?.includeLocalFundNote ?? filters.includeLocalFund}
          categoryLabel={activeViewModel?.categoryLabel || "All Categories"}
        />
      ) : (
        <AnalyticsBreakdownTable
          view={filters.view}
          rows={activeViewModel?.rows ?? []}
          loading={chartLoading}
          error={chartError}
          compareYear={filters.compareYear}
        />
      )}

      <AnalyticsRebuildModal
        isOpen={rebuildState.modalOpen}
        scope={rebuildState.scope}
        submitting={rebuildState.submitting}
        errorMessage={rebuildState.errorMessage}
        onClose={closeRebuildModal}
        onConfirm={handleConfirmRebuild}
      />
      </div>
    </div>
  );
};

export default AdminDashboardPage;
